import { Formik, Form } from "formik";
import { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { FormInput } from "@volca/ui/components/ui/form-input";
import {
  InfoWithLabel,
  InfoWithLabelSkeleton,
} from "@volca/ui/components/ui/info-with-label";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useIsMobile } from "@volca/ui/hooks/use-mobile";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { isProductEnabled } from "@/business/product-access";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { getInitials } from "@/core/utils/name";
import { roleLabel } from "@/user/constants";
import { useEditMyUser, useGetMyUser } from "@/user/query";
import { isApOnly } from "@/user/utils";

const profileSchema = Yup.object().shape({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
});

export default function Profile() {
  const { data: userInfo, isLoading } = useGetMyUser();
  const isMobile = useIsMobile();
  const { settings, isLoading: isProductsLoading } = useBusinessProducts();
  const apOnly = isApOnly(userInfo?.role);

  const { mutate: editUser, isPending } = useEditMyUser();

  const initialValues = useMemo(
    () => ({
      first_name: userInfo?.first_name || "",
      last_name: userInfo?.last_name || "",
    }),
    [userInfo],
  );

  const handleSubmit = useCallback(
    (values: typeof initialValues) => {
      editUser(
        {
          first_name: values.first_name,
          last_name: values.last_name,
        },
        {
          onSuccess: () => {
            toast.success("Profile saved");
          },
          onError: (error) => {
            toast.error(
              error instanceof Error
                ? error.message
                : "Couldn't save your profile. Please try again.",
            );
          },
        },
      );
    },
    [editUser, toast],
  );

  return (
    <PageContainer>
      <PageHeader title="Profile" />
      {/* A form: narrower than the frame, aligned left. */}
      <Stack align="start" className="w-full max-w-xl" gap={2}>
        {apOnly &&
          !isProductsLoading &&
          !isProductEnabled(settings, "accounts_payable") && (
            <Text tone="muted">
              Accounts Payable is not enabled for this business. Contact your
              administrator for access.
            </Text>
          )}
        <Card
          className={cn("w-full gap-0 p-6", {
            "p-4": isMobile,
          })}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={profileSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ errors, touched, getFieldProps, isValid, dirty }) => (
              <Form>
                <Stack
                  align="start"
                  gap={4}
                  className={cn("w-full", "gap-y-8", {
                    "gap-y-6": isMobile,
                  })}
                >
                  <Stack
                    className="w-full"
                    align={isMobile ? "center" : "start"}
                    direction={isMobile ? "column" : "row"}
                    gap={isMobile ? 4 : 8}
                    justify="between"
                  >
                    <Stack
                      align="center"
                      justify="center"
                      gap={0}
                      className={cn("h-24 w-24 rounded-full bg-neutral", {
                        "h-20 w-20": isMobile,
                      })}
                    >
                      <Text
                        weight="normal"
                        variant="display"
                        className="text-ink"
                      >
                        {getInitials(userInfo)}
                      </Text>
                    </Stack>
                    <Stack
                      align="start"
                      gap={6}
                      className={cn("flex-1 pb-4", {
                        "w-full": isMobile,
                      })}
                    >
                      <Stack
                        className="w-full"
                        align="start"
                        direction={isMobile ? "column" : "row"}
                        justify="between"
                        gap={isMobile ? 4 : 6}
                      >
                        <FormInput
                          id="first_name"
                          type="text"
                          label="First name"
                          error={
                            touched.first_name ? errors.first_name : undefined
                          }
                          {...getFieldProps("first_name")}
                          required
                          disableRequirementAsterisk
                          fullWidth
                        />
                        <FormInput
                          id="last_name"
                          type="text"
                          label="Last name"
                          error={
                            touched.last_name ? errors.last_name : undefined
                          }
                          {...getFieldProps("last_name")}
                          required
                          disableRequirementAsterisk
                          fullWidth
                        />
                      </Stack>

                      {isLoading ? (
                        <>
                          <InfoWithLabelSkeleton contentWidth="w-48" />
                          <InfoWithLabelSkeleton />
                        </>
                      ) : (
                        <>
                          <InfoWithLabel
                            label="Email"
                            subMessage="Contact support to change your email."
                          >
                            {userInfo?.email ?? "-"}
                          </InfoWithLabel>
                          <InfoWithLabel
                            label="Role"
                            subMessage="A manager can change your role in Settings, under Users."
                          >
                            {userInfo?.role ? roleLabel(userInfo.role) : "-"}
                          </InfoWithLabel>
                        </>
                      )}
                    </Stack>
                  </Stack>

                  <Button
                    type="submit"
                    size="sm"
                    disabled={!isValid || !dirty}
                    loading={isPending}
                    loadingLabel="Save changes, in progress"
                    className={cn({ "w-full": isMobile })}
                  >
                    Save changes
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Card>
      </Stack>
    </PageContainer>
  );
}
